// apps/patient/src/components/profile/PatientProfileEdit.tsx

import { useState } from 'react';
import { 
  User, 
  Phone, 
  Mail, 
  MapPin, 
  Bell, 
  AlertCircle,
  Plus,
  Trash2,
  Save,
  X,
  Shield,
  MessageSquare
} from 'lucide-react';

// US States list with full names and abbreviations
const US_STATES = [
  { name: 'Alabama', abbr: 'AL' },
  { name: 'Alaska', abbr: 'AK' },
  { name: 'Arizona', abbr: 'AZ' },
  { name: 'Arkansas', abbr: 'AR' },
  { name: 'California', abbr: 'CA' },
  { name: 'Colorado', abbr: 'CO' },
  { name: 'Connecticut', abbr: 'CT' },
  { name: 'Delaware', abbr: 'DE' },
  { name: 'District of Columbia', abbr: 'DC' },
  { name: 'Florida', abbr: 'FL' },
  { name: 'Georgia', abbr: 'GA' },
  { name: 'Hawaii', abbr: 'HI' },
  { name: 'Idaho', abbr: 'ID' },
  { name: 'Illinois', abbr: 'IL' },
  { name: 'Indiana', abbr: 'IN' },
  { name: 'Iowa', abbr: 'IA' },
  { name: 'Kansas', abbr: 'KS' },
  { name: 'Kentucky', abbr: 'KY' },
  { name: 'Louisiana', abbr: 'LA' },
  { name: 'Maine', abbr: 'ME' },
  { name: 'Maryland', abbr: 'MD' },
  { name: 'Massachusetts', abbr: 'MA' },
  { name: 'Michigan', abbr: 'MI' },
  { name: 'Minnesota', abbr: 'MN' },
  { name: 'Mississippi', abbr: 'MS' },
  { name: 'Missouri', abbr: 'MO' },
  { name: 'Montana', abbr: 'MT' },
  { name: 'Nebraska', abbr: 'NE' },
  { name: 'Nevada', abbr: 'NV' },
  { name: 'New Hampshire', abbr: 'NH' },
  { name: 'New Jersey', abbr: 'NJ' },
  { name: 'New Mexico', abbr: 'NM' },
  { name: 'New York', abbr: 'NY' },
  { name: 'North Carolina', abbr: 'NC' },
  { name: 'North Dakota', abbr: 'ND' },
  { name: 'Ohio', abbr: 'OH' },
  { name: 'Oklahoma', abbr: 'OK' },
  { name: 'Oregon', abbr: 'OR' },
  { name: 'Pennsylvania', abbr: 'PA' },
  { name: 'Rhode Island', abbr: 'RI' },
  { name: 'South Carolina', abbr: 'SC' },
  { name: 'South Dakota', abbr: 'SD' },
  { name: 'Tennessee', abbr: 'TN' },
  { name: 'Texas', abbr: 'TX' },
  { name: 'Utah', abbr: 'UT' },
  { name: 'Vermont', abbr: 'VT' },
  { name: 'Virginia', abbr: 'VA' },
  { name: 'Washington', abbr: 'WA' },
  { name: 'West Virginia', abbr: 'WV' },
  { name: 'Wisconsin', abbr: 'WI' },
  { name: 'Wyoming', abbr: 'WY' }
];

interface PatientProfileEditProps {
  user: any;
  onSave: (data: any) => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
}

export function PatientProfileEdit({ user, onSave, onCancel, isSaving }: PatientProfileEditProps) {
  const patient = user?.patient;
  
  // Initialize form data
  const [formData, setFormData] = useState({
    phone: user.phone || patient?.phone || '',
    email_preferences: patient?.email_preferences ?? true,
    sms_preferences: patient?.sms_preferences ?? true,
    biometric_mfa: user.biometric_mfa || false,
    address: patient?.address || {
      line1: '',
      line2: '',
      city: '',
      state: '',
      zip: ''
    },
    emergency_contacts: patient?.emergency_contacts || []
  });

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddressChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value
      }
    }));
  };

  const handleAddEmergencyContact = () => {
    setFormData(prev => ({
      ...prev,
      emergency_contacts: [
        ...prev.emergency_contacts,
        {
          name: '',
          relationship: '',
          phone: '',
          email: '',
          is_primary: prev.emergency_contacts.length === 0
        }
      ]
    }));
  };

  const handleEmergencyContactChange = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      emergency_contacts: prev.emergency_contacts.map((contact: any, i: number) => 
        i === index ? { ...contact, [field]: value } : contact
      )
    }));
  };

  const handleRemoveEmergencyContact = (index: number) => {
    setFormData(prev => ({
      ...prev,
      emergency_contacts: prev.emergency_contacts.filter((_: any, i: number) => i !== index)
    }));
  };

  const handleSetPrimaryContact = (index: number) => {
    setFormData(prev => ({
      ...prev,
      emergency_contacts: prev.emergency_contacts.map((contact: any, i: number) => ({
        ...contact,
        is_primary: i === index
      }))
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="divide-y divide-gray-200">
      {/* Contact Information */}
      <div className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Phone className="w-5 h-5 mr-2 text-gray-400" />
          Contact Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
              placeholder="(555) 123-4567"
            />
          </div>
        </div>
      </div>

      {/* Address */}
      <div className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <MapPin className="w-5 h-5 mr-2 text-gray-400" />
          Address
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Street Address
            </label>
            <input
              type="text"
              value={formData.address.line1}
              onChange={(e) => handleAddressChange('line1', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
              placeholder="123 Main Street"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Apartment, Suite, etc. (Optional)
            </label>
            <input
              type="text"
              value={formData.address.line2}
              onChange={(e) => handleAddressChange('line2', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
              placeholder="Apt 4B"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <input
                type="text"
                value={formData.address.city}
                onChange={(e) => handleAddressChange('city', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                placeholder="New York"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State
              </label>
              <select
                value={formData.address.state}
                onChange={(e) => handleAddressChange('state', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                <option value="">Select state</option>
                {US_STATES.map((state) => (
                  <option key={state.abbr} value={state.abbr}>
                    {state.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ZIP Code
              </label>
              <input
                type="text"
                value={formData.address.zip}
                onChange={(e) => handleAddressChange('zip', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                placeholder="10001"
                maxLength={10}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Emergency Contacts */}
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <AlertCircle className="w-5 h-5 mr-2 text-gray-400" />
            Emergency Contacts
          </h3>
          <button
            type="button"
            onClick={handleAddEmergencyContact}
            className="flex items-center text-sm text-primary hover:text-primary/80"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Contact
          </button>
        </div>
        
        {formData.emergency_contacts.length === 0 ? (
          <p className="text-gray-500 text-sm">No emergency contacts added</p>
        ) : (
          <div className="space-y-4">
            {formData.emergency_contacts.map((contact: any, index: number) => (
              <div key={index} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <h4 className="font-medium text-gray-900">Contact {index + 1}</h4>
                  <div className="flex items-center space-x-2">
                    <label className="flex items-center text-sm">
                      <input
                        type="radio"
                        checked={contact.is_primary}
                        onChange={() => handleSetPrimaryContact(index)}
                        className="mr-1"
                      />
                      Primary
                    </label>
                    <button
                      type="button"
                      onClick={() => handleRemoveEmergencyContact(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      value={contact.name}
                      onChange={(e) => handleEmergencyContactChange(index, 'name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Relationship
                    </label>
                    <input
                      type="text"
                      value={contact.relationship}
                      onChange={(e) => handleEmergencyContactChange(index, 'relationship', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                      placeholder="Spouse, Parent, etc."
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={contact.phone}
                      onChange={(e) => handleEmergencyContactChange(index, 'phone', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                      placeholder="(555) 123-4567"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email (Optional)
                    </label>
                    <input
                      type="email"
                      value={contact.email}
                      onChange={(e) => handleEmergencyContactChange(index, 'email', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Notifications & Security */}
      <div className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Bell className="w-5 h-5 mr-2 text-gray-400" />
          Notifications & Security
        </h3>
        <div className="space-y-4">
          {/* Email Notifications */}
          <label className="flex items-center justify-between cursor-pointer p-3 border rounded-lg hover:bg-gray-50">
            <div className="flex items-center">
              <Mail className="w-5 h-5 mr-3 text-gray-400" />
              <div>
                <span className="text-gray-900 font-medium">Email Notifications</span>
                <p className="text-sm text-gray-500">Receive updates and reminders via email</p>
              </div>
            </div>
            <input
              type="checkbox"
              checked={formData.email_preferences}
              onChange={(e) => handleInputChange('email_preferences', e.target.checked)}
              className="w-5 h-5 text-primary focus:ring-primary/50 border-gray-300 rounded"
            />
          </label>
          
          {/* SMS Notifications */}
          <label className="flex items-center justify-between cursor-pointer p-3 border rounded-lg hover:bg-gray-50">
            <div className="flex items-center">
              <MessageSquare className="w-5 h-5 mr-3 text-gray-400" />
              <div>
                <span className="text-gray-900 font-medium">Text Message Notifications</span>
                <p className="text-sm text-gray-500">Get important alerts via SMS</p>
              </div>
            </div>
            <input
              type="checkbox"
              checked={formData.sms_preferences}
              onChange={(e) => handleInputChange('sms_preferences', e.target.checked)}
              className="w-5 h-5 text-primary focus:ring-primary/50 border-gray-300 rounded"
            />
          </label>
          
          {/* Biometric MFA - Only show if currently enabled */}
          {user.biometric_mfa && (
            <label className="flex items-center justify-between cursor-pointer p-3 border rounded-lg hover:bg-gray-50">
              <div className="flex items-center">
                <Shield className="w-5 h-5 mr-3 text-gray-400" />
                <div>
                  <span className="text-gray-900 font-medium">Biometric Authentication</span>
                  <p className="text-sm text-gray-500">Use face recognition for secure login</p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={formData.biometric_mfa}
                onChange={(e) => handleInputChange('biometric_mfa', e.target.checked)}
                className="w-5 h-5 text-primary focus:ring-primary/50 border-gray-300 rounded"
              />
            </label>
          )}
        </div>
      </div>

      {/* Form Actions */}
      <div className="p-6 bg-gray-50 flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSaving}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center"
        >
          <X className="w-4 h-4 mr-2" />
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSaving}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center"
        >
          {isSaving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </>
          )}
        </button>
      </div>
    </form>
  );
}